import { StateCreator } from 'zustand';
import { QuotationStore } from '../quotationStore.types';
import { calculateProjectFinancials } from '@/utils/financials';
import { debounce } from '@/lib/utils';
import { PaymentMilestone, isImmutableStatus } from '@/types/quotation';
import { settingsService } from '@/services/settingsService';
import { PRICING_ENGINE_VERSION } from '@/utils/pricingEngine';

export interface FinancialSlice {
    recalculateFinancials: () => void;
    triggerRecalculateDebounced: () => void;
    updatePaymentSchedule: (milestones: PaymentMilestone[]) => void;
    resetPaymentSchedule: () => void;
}

export const createFinancialSlice: StateCreator<
    QuotationStore,
    [],
    [],
    FinancialSlice
> = (set, get) => {
    // Scoped per slice instance — not shared across store instances
    const instanceDebouncedCalc = debounce(() => {
        get().recalculateFinancials();
    }, 500);

    return {
    recalculateFinancials: () => {
        const { currentQuotation } = get();
        if (!currentQuotation) return;

        const isLocked = currentQuotation.metadata.locked || isImmutableStatus(currentQuotation.metadata.status);
        // For locked/finalized quotes: only allow recalculation if grandTotal is actually 0
        // (i.e. the DB had a zeroed value and we need to recover it from line items).
        // Never recalculate downward over a positive, correct finalized total.
        if (isLocked && currentQuotation.financials && currentQuotation.financials.grandTotal > 0) return;

        // Pull payment schedule rules from settings (generic, not hardcoded)
        const scheduleRules = settingsService.getPaymentScheduleRules();

        const result = calculateProjectFinancials(
            currentQuotation.sections,
            currentQuotation.financials,
            currentQuotation.paymentSchedule || [],
            scheduleRules.length > 0 ? scheduleRules : null,
        );

        const scheduleChanged = JSON.stringify(currentQuotation.paymentSchedule) !== JSON.stringify(result.paymentSchedule);
        const hasChanged =
            currentQuotation.financials.subtotal !== result.subtotal ||
            currentQuotation.financials.taxAmount !== result.taxAmount ||
            currentQuotation.financials.grandTotal !== result.grandTotal ||
            currentQuotation.financials.discountAmount !== result.discountAmount ||
            scheduleChanged;

        if (hasChanged) {
            set({
                currentQuotation: {
                    ...currentQuotation,
                    financials: {
                        ...currentQuotation.financials,
                        subtotal: result.subtotal,
                        taxAmount: result.taxAmount,
                        grandTotal: result.grandTotal,
                        discountAmount: result.discountAmount,
                        provenance: {
                            pricingEngineVersion: PRICING_ENGINE_VERSION,
                            calculationTimestamp: new Date().toISOString(),
                            currency: 'INR',
                            gstMode: 'exclusive',
                            roundingStrategy: 'ROUND_HALF_UP'
                        }
                    },
                    paymentSchedule: result.paymentSchedule,
                }
            });
        }
    },

    triggerRecalculateDebounced: () => {
        instanceDebouncedCalc();
    },

    updatePaymentSchedule: (milestones) => {
        const { currentQuotation } = get();
        if (!currentQuotation) return;
        
        const isLocked = currentQuotation.metadata.locked || isImmutableStatus(currentQuotation.metadata.status);
        if (isLocked) {
            console.warn('[FinancialSlice] Blocked payment schedule update: quotation is locked/finalized.');
            return;
        }

        set({ past: [...get().past, currentQuotation], future: [] });
        set({
            currentQuotation: {
                ...currentQuotation,
                paymentSchedule: milestones,
                financials: {
                    ...currentQuotation.financials,
                    paymentScheduleAuto: false
                }
            }
        });
    },

    resetPaymentSchedule: () => {
        const { currentQuotation } = get();
        if (!currentQuotation) return;
        
        const isLocked = currentQuotation.metadata.locked || isImmutableStatus(currentQuotation.metadata.status);
        if (isLocked) {
            console.warn('[FinancialSlice] Blocked payment schedule reset: quotation is locked/finalized.');
            return;
        }

        set({
            currentQuotation: {
                ...currentQuotation,
                financials: {
                    ...currentQuotation.financials,
                    paymentScheduleAuto: true
                }
            }
        });
        get().recalculateFinancials();
    },
    };
};
